/**
 * Shared presentation helpers for the WhatsApp connect failure screens
 * (INT-1217). Kept out of the components so `ConnectionFailedClosed` and
 * `ConnectionEscalated` select copy identically.
 */

/**
 * Display bound for Meta's `error_message`.
 *
 * whatsapp-business-service already clamps the stored value to 2000 characters
 * (`maxEmbeddedSignupMessageLength`), so this is purely about not letting an
 * unexpectedly long sentence blow out a centered `max-w-lg` block — it is not
 * truncation parity with the backend.
 */
export const MAX_META_MESSAGE_DISPLAY_LENGTH = 400;

/**
 * Meta's own customer-facing sentence, ready to render.
 *
 * Returned verbatim (only trimmed and length-bounded) because Meta already
 * localizes it — the real payload reads "Tu número de teléfono no está
 * registrado con WhatsApp Business…". Paraphrasing or translating it would be
 * strictly worse than quoting the party that rejected the connection.
 *
 * Returns `null` for absent, empty and whitespace-only input so callers can fall
 * back to catalog copy with a single truthiness check.
 *
 * Slicing is code-point aware (`Array.from`, not `String.prototype.slice`) so a
 * bound landing mid-character cannot emit a broken surrogate pair — the same
 * class of bug the backend fixed with its rune-aware clamp.
 */
export function formatMetaReportedMessage(
  value: string | null | undefined,
): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const characters = Array.from(trimmed);
  if (characters.length <= MAX_META_MESSAGE_DISPLAY_LENGTH) return trimmed;
  return `${characters.slice(0, MAX_META_MESSAGE_DISPLAY_LENGTH).join('')}…`;
}

/**
 * Whether Meta actually told us where the customer stopped.
 *
 * Distinct from "the catalog resolved an entry": the catalog always resolves
 * something (that is its contract), so callers need this to tell an unreported
 * step — which must keep today's generic copy — apart from a reported one.
 */
export function hasReportedSignupStep(step: string | null | undefined): boolean {
  return typeof step === 'string' && step.trim().length > 0;
}
